/**
 * @fileoverview Componentes visuais compartilhados entre CreateLeadModal e
 * LeadDetailModal — mesma estética premium em criação e edição.
 *
 * Exportados:
 *  - UserAvatar               → avatar com foto real OU fallback de iniciais
 *  - AssigneePicker           → popover com busca + avatares
 *  - PrioritySegmented        → segmented control de prioridade
 *  - SlaPopoverButton         → trigger compacto + popover (presets + datetime)
 *  - CardTitleHero            → input grande sem borda, validação tardia
 *  - CardDescriptionEditor    → Quill wrapper estilizado dark-first
 *  - CardCustomFieldsSection  → lista de fields dinâmicos + popover de add
 *  - Constants: PRIORITIES, SLA_PRESETS, FIELD_TYPES, PRESETS, QUILL_*
 *  - Helpers: computeSlaStatus, slaPreset, formatSlaShort, dateToLocalInput,
 *             quillIsEmpty, generateFieldId
 *
 * Todos usam tema dark-first do projeto.
 */

import React, { useState, useEffect, useMemo, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Popover from "@material-ui/core/Popover";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import Checkbox from "@material-ui/core/Checkbox";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import InputMask from "react-input-mask";
import NumberFormat from "react-number-format";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  Calendar,
  Clock,
  AlertCircle,
  CheckCircle2,
  Search,
  Check,
  UserRound,
  X,
  Plus,
  Eye,
  Pencil,
  Trash2,
  MoreVertical,
  Type as TypeIcon,
  MessageCircle as WhatsappIconLc,
  IdCard as CpfIconLc,
  Mail as EmailIconLc,
  DollarSign as CurrencyIconLc,
  ListChecks as ChecklistIconLc,
  Users2 as UsersIconLc,
} from "lucide-react";
import { stringToColor, initials } from "../UsersTable";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers exportados
// ─────────────────────────────────────────────────────────────────────────────
export const PRIORITIES = [
  { value: "low", label: "Baixa", color: "#22c55e", hint: "Sem urgência, pode aguardar" },
  { value: "normal", label: "Média", color: "#3b82f6", hint: "Fluxo normal de trabalho" },
  { value: "high", label: "Alta", color: "#f59e0b", hint: "Precisa de atenção" },
  { value: "urgent", label: "Maior", color: "#dc2626", hint: "Crítico, atendimento imediato" },
];

export const SLA_PRESETS = [
  { key: "today", label: "Hoje", offsetDays: 0, hour: 18 },
  { key: "tomorrow", label: "Amanhã", offsetDays: 1, hour: 18 },
  { key: "two", label: "+2 dias", offsetDays: 2, hour: 18 },
  { key: "seven", label: "+7 dias", offsetDays: 7, hour: 18 },
];

const pad = (n) => String(n).padStart(2, "0");

export const dateToLocalInput = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;

export const slaPreset = (days, hour = 18, minute = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  d.setHours(hour, minute, 0, 0);
  return dateToLocalInput(d);
};

export const formatSlaShort = (isoDatetime) => {
  if (!isoDatetime) return "";
  const d = new Date(isoDatetime);
  if (Number.isNaN(d.getTime())) return "";
  const dateStr = d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit" });
  const timeStr = d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  return `${dateStr} às ${timeStr}`;
};

export const computeSlaStatus = (isoDatetime) => {
  if (!isoDatetime) return null;
  const target = new Date(isoDatetime);
  if (Number.isNaN(target.getTime())) return null;

  const now = new Date();
  const diffMs = target.getTime() - now.getTime();
  const diffDays = Math.round(diffMs / 86400000);
  const diffHours = Math.round(diffMs / 3600000);

  if (diffMs < 0) {
    const absDays = Math.abs(diffDays);
    return {
      tone: "overdue",
      color: "#ef4444",
      bg: "rgba(239,68,68,0.12)",
      border: "rgba(239,68,68,0.30)",
      icon: AlertCircle,
      label:
        absDays === 0
          ? `Vencido há ${Math.abs(diffHours)}h`
          : absDays === 1
            ? "Vencido há 1 dia"
            : `Vencido há ${absDays} dias`,
    };
  }
  if (diffMs < 24 * 3600 * 1000) {
    return {
      tone: "today",
      color: "#f59e0b",
      bg: "rgba(245,158,11,0.14)",
      border: "rgba(245,158,11,0.32)",
      icon: Clock,
      label:
        diffHours <= 1
          ? "Vence em menos de 1h"
          : diffHours < 6
            ? `Vence em ${diffHours}h`
            : "Vence hoje",
    };
  }
  if (diffDays <= 3) {
    return {
      tone: "soon",
      color: "#f59e0b",
      bg: "rgba(245,158,11,0.10)",
      border: "rgba(245,158,11,0.26)",
      icon: Clock,
      label: diffDays === 1 ? "Vence amanhã" : `Vence em ${diffDays} dias`,
    };
  }
  return {
    tone: "ok",
    color: "#22c55e",
    bg: "rgba(34,197,94,0.10)",
    border: "rgba(34,197,94,0.24)",
    icon: CheckCircle2,
    label: `${diffDays} dias até o prazo`,
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// Estilos compartilhados
// ─────────────────────────────────────────────────────────────────────────────
export const useLeadSharedStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    // Avatar
    avatar: {
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: 700,
      flexShrink: 0,
      overflow: "hidden",
      position: "relative",
    },
    avatarImg: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
    avatarPlaceholder: {
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      background: dark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.05)",
      color: dark ? "rgba(255,255,255,0.4)" : "#94A3B8",
    },

    // Assignee picker
    assigneeTrigger: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      height: 40,
      padding: "0 12px",
      borderRadius: 11,
      border: dark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(15,23,42,0.10)",
      background: dark ? "rgba(255,255,255,0.025)" : "#FFFFFF",
      cursor: "pointer",
      transition: "border-color .15s, background .15s",
      width: "100%",
      fontFamily: "inherit",
      "&:hover": {
        borderColor: dark ? "rgba(91,124,255,0.32)" : "rgba(61,92,253,0.28)",
        background: dark ? "rgba(255,255,255,0.04)" : "#FFFFFF",
      },
    },
    assigneeName: {
      flex: 1,
      minWidth: 0,
      fontSize: 13,
      fontWeight: 500,
      color: dark ? "#FAFAFA" : "#0F172A",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      textAlign: "left",
    },
    assigneeNamePlaceholder: { color: dark ? "rgba(255,255,255,0.42)" : "#94A3B8", fontWeight: 400 },
    assigneeChevron: {
      color: dark ? "rgba(255,255,255,0.4)" : "#94A3B8",
      flexShrink: 0,
    },

    // Picker popover (compartilhado entre Assignee + SLA + add-field)
    pickerPopover: {
      "& .MuiPopover-paper": {
        borderRadius: 14,
        background: dark ? "#1C1F26" : "#FFFFFF",
        border: dark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.08)",
        boxShadow: dark
          ? "0 20px 60px -12px rgba(0,0,0,0.6), 0 8px 24px -6px rgba(0,0,0,0.4)"
          : "0 20px 60px -12px rgba(15,23,42,0.18), 0 8px 24px -6px rgba(15,23,42,0.10)",
        width: 320,
        maxHeight: 380,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      },
    },
    pickerSearchWrap: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "10px 12px",
      borderBottom: dark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
    },
    pickerSearchIcon: { color: dark ? "rgba(255,255,255,0.45)" : "#94A3B8", flexShrink: 0 },
    pickerSearchInput: {
      flex: 1,
      border: "none",
      background: "transparent",
      outline: "none",
      fontSize: 13,
      fontFamily: "inherit",
      color: dark ? "#FAFAFA" : "#0F172A",
      "&::placeholder": { color: dark ? "rgba(255,255,255,0.32)" : "#94A3B8" },
    },
    pickerList: { flex: 1, overflowY: "auto", padding: 6 },
    pickerOption: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "8px 10px",
      borderRadius: 9,
      cursor: "pointer",
      transition: "background .12s",
      border: "none",
      background: "transparent",
      width: "100%",
      textAlign: "left",
      fontFamily: "inherit",
      "&:hover": { background: dark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.04)" },
    },
    pickerOptionActive: {
      background: dark ? "rgba(91,124,255,0.14)" : "rgba(61,92,253,0.08)",
      "&:hover": {
        background: dark ? "rgba(91,124,255,0.20)" : "rgba(61,92,253,0.12)",
      },
    },
    pickerOptionText: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1 },
    pickerOptionName: {
      fontSize: 13,
      fontWeight: 500,
      color: dark ? "#FAFAFA" : "#0F172A",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    pickerOptionMeta: {
      fontSize: 11,
      color: dark ? "rgba(255,255,255,0.45)" : "#94A3B8",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    pickerCheck: { color: dark ? "#A5B4FC" : "#3D5CFD", flexShrink: 0 },
    pickerEmpty: {
      padding: "24px 16px",
      textAlign: "center",
      fontSize: 12.5,
      color: dark ? "rgba(255,255,255,0.45)" : "#94A3B8",
    },

    // Priority segmented
    prioritySegment: {
      display: "grid",
      gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
      gap: 4,
      padding: 4,
      borderRadius: 11,
      background: dark ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.04)",
      border: dark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
    },
    priorityPill: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 4,
      height: 30,
      padding: "0 6px",
      borderRadius: 8,
      border: "none",
      background: "transparent",
      color: dark ? "rgba(255,255,255,0.55)" : "#71717A",
      fontSize: 11,
      fontWeight: 600,
      cursor: "pointer",
      transition: "background .12s, color .12s",
      fontFamily: "inherit",
      minWidth: 0,
      whiteSpace: "nowrap",
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)",
        color: dark ? "#FAFAFA" : "#0F172A",
      },
    },
    priorityPillActive: {
      background: dark ? "rgba(255,255,255,0.07)" : "#FFFFFF",
      color: dark ? "#FAFAFA" : "#0F172A",
      // Ring INSET (projeta pra dentro) — sem essa mudança o "Maior" (último
      // pill) estourava o container quando ativo, porque o ring outer 0 0 0 1px
      // ia 1px pra fora do segmented + padding 4 + border 1px do parent.
      boxShadow: dark
        ? "0 1px 2px rgba(0,0,0,0.30), inset 0 0 0 1px rgba(255,255,255,0.04)"
        : "0 1px 2px rgba(15,23,42,0.06), inset 0 0 0 1px rgba(15,23,42,0.04)",
    },
    priorityDot: { width: 6, height: 6, borderRadius: "50%", flexShrink: 0 },

    // SLA trigger
    slaTrigger: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      width: "100%",
      height: 40,
      padding: "0 12px",
      borderRadius: 11,
      border: dark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(15,23,42,0.10)",
      background: dark ? "rgba(255,255,255,0.025)" : "#FFFFFF",
      cursor: "pointer",
      transition: "border-color .15s, background .15s",
      fontFamily: "inherit",
      "&:hover": {
        borderColor: dark ? "rgba(91,124,255,0.32)" : "rgba(61,92,253,0.28)",
        background: dark ? "rgba(255,255,255,0.04)" : "#FFFFFF",
      },
    },
    slaTriggerText: {
      flex: 1,
      minWidth: 0,
      fontSize: 13,
      fontWeight: 500,
      color: dark ? "#FAFAFA" : "#0F172A",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      textAlign: "left",
    },
    slaTriggerPlaceholder: { color: dark ? "rgba(255,255,255,0.42)" : "#94A3B8", fontWeight: 400 },
    slaClearInlineBtn: {
      width: 22,
      height: 22,
      border: "none",
      borderRadius: 6,
      background: "transparent",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      color: dark ? "rgba(255,255,255,0.5)" : "#94A3B8",
      transition: "background .12s, color .12s",
      flexShrink: 0,
      "&:hover": {
        background: "rgba(239,68,68,0.12)",
        color: "#ef4444",
      },
    },
    // SLA popover content
    slaPopoverHeader: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "12px 14px 10px",
      borderBottom: dark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
    },
    slaPopoverTitle: {
      flex: 1,
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      color: dark ? "rgba(255,255,255,0.65)" : "#475569",
    },
    slaPopoverBody: {
      padding: "12px 14px",
      display: "flex",
      flexDirection: "column",
      gap: 12,
    },
    slaPresetsRow: { display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 6 },
    slaPresetCard: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "9px 12px",
      borderRadius: 9,
      border: dark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(15,23,42,0.08)",
      background: "transparent",
      color: dark ? "rgba(255,255,255,0.75)" : "#475569",
      fontSize: 12.5,
      fontWeight: 500,
      cursor: "pointer",
      transition: "background .12s, border-color .12s, color .12s",
      fontFamily: "inherit",
      textAlign: "left",
      "&:hover": {
        background: dark ? "rgba(91,124,255,0.10)" : "rgba(61,92,253,0.06)",
        borderColor: dark ? "rgba(91,124,255,0.32)" : "rgba(61,92,253,0.28)",
        color: dark ? "#FAFAFA" : "#0F172A",
      },
    },
    slaPresetCardActive: {
      background: dark ? "rgba(91,124,255,0.16)" : "rgba(61,92,253,0.08)",
      borderColor: dark ? "rgba(91,124,255,0.45)" : "rgba(61,92,253,0.32)",
      color: dark ? "#A5B4FC" : "#3D5CFD",
    },
    slaCustomLabel: {
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: dark ? "rgba(255,255,255,0.42)" : "#94A3B8",
      marginBottom: 6,
    },
    slaCustomInput: {
      width: "100%",
      "& .MuiOutlinedInput-root": {
        height: 36,
        borderRadius: 9,
        background: dark ? "rgba(255,255,255,0.025)" : "#FFFFFF",
        fontSize: 13,
        color: dark ? "#FAFAFA" : "#0F172A",
      },
      "& fieldset": {
        borderColor: dark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.10)",
      },
      "& .MuiOutlinedInput-input": { padding: "8px 12px" },
      "& input[type=datetime-local]::-webkit-calendar-picker-indicator": {
        filter: dark ? "invert(0.7)" : "none",
        cursor: "pointer",
        opacity: 0.7,
      },
    },
    slaPopoverFooter: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "10px 14px",
      borderTop: dark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
      background: dark ? "rgba(0,0,0,0.2)" : "rgba(15,23,42,0.02)",
    },
    slaPopoverStatus: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "5px 10px",
      borderRadius: 7,
      fontSize: 11.5,
      fontWeight: 600,
      border: "1px solid",
      whiteSpace: "nowrap",
      flex: 1,
      minWidth: 0,
      overflow: "hidden",
      "& span": {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
    },
    slaPopoverClearBtn: {
      height: 28,
      padding: "0 12px",
      borderRadius: 7,
      border: dark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(15,23,42,0.10)",
      background: "transparent",
      color: dark ? "rgba(255,255,255,0.65)" : "#71717A",
      fontSize: 11.5,
      fontWeight: 500,
      cursor: "pointer",
      transition: "color .12s, background .12s, border-color .12s",
      fontFamily: "inherit",
      whiteSpace: "nowrap",
      "&:hover": {
        color: "#ef4444",
        borderColor: "rgba(239,68,68,0.32)",
        background: "rgba(239,68,68,0.06)",
      },
    },

    // Detail field cell — label uppercase em cima
    detailCell: { display: "flex", flexDirection: "column", gap: 7, minWidth: 0 },
    detailCellLabel: {
      fontSize: 10.5,
      fontWeight: 700,
      color: dark ? "rgba(255,255,255,0.45)" : "#71717A",
      letterSpacing: "0.06em",
      textTransform: "uppercase",
    },

    // ── Section label (uppercase tiny) ──
    sectionLabel: {
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: dark ? "rgba(255,255,255,0.42)" : "#71717A",
    },
    sectionHeaderRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 10,
      gap: 8,
    },

    // ── Field input (premium TextField) ──
    fieldInput: {
      "& .MuiOutlinedInput-root": {
        borderRadius: 11,
        background: dark ? "rgba(255,255,255,0.025)" : "#FFFFFF",
        transition: "border-color .15s, box-shadow .15s, background .15s",
        fontSize: 14,
        "& fieldset": {
          borderColor: dark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.10)",
          transition: "border-color .15s",
        },
        "&:hover fieldset": {
          borderColor: dark ? "rgba(91,124,255,0.32)" : "rgba(61,92,253,0.30)",
        },
        "&.Mui-focused": { background: dark ? "rgba(255,255,255,0.04)" : "#FFFFFF" },
        "&.Mui-focused fieldset": {
          borderColor: dark ? "#5B7CFF" : "#3D5CFD",
          borderWidth: 1,
          boxShadow: dark
            ? "0 0 0 3px rgba(91,124,255,0.14)"
            : "0 0 0 3px rgba(61,92,253,0.10)",
        },
        "&.Mui-error fieldset": { borderColor: "#ef4444" },
      },
      "& .MuiOutlinedInput-input": { padding: "12px 14px" },
      "& .MuiOutlinedInput-multiline": { padding: "12px 14px" },
    },
    fieldError: {
      fontSize: 11.5,
      color: "#ef4444",
      marginTop: 5,
      display: "flex",
      alignItems: "center",
      gap: 5,
    },

    // ── Card title HERO (input grande sem borda) ──
    titleHero: {
      width: "100%",
      "& .MuiOutlinedInput-root": {
        background: "transparent",
        padding: 0,
        "& fieldset": { border: "none" },
        "&:hover fieldset": { border: "none" },
        "&.Mui-focused fieldset": { border: "none", boxShadow: "none" },
        "&.Mui-focused": { background: "transparent" },
      },
      "& .MuiOutlinedInput-input": {
        fontSize: 22,
        fontWeight: 600,
        letterSpacing: "-0.4px",
        lineHeight: 1.3,
        padding: "4px 0 8px",
        color: dark ? "#FAFAFA" : "#0F172A",
      },
      "& .MuiOutlinedInput-input::placeholder": {
        color: dark ? "rgba(255,255,255,0.28)" : "rgba(15,23,42,0.32)",
        opacity: 1,
        fontWeight: 500,
      },
    },
    titleHeroError: {
      "& .MuiOutlinedInput-input": {
        boxShadow: `inset 0 -1.5px 0 #ef4444`,
      },
    },

    // ── Quill description editor wrapper ──
    quillWrap: {
      borderRadius: 11,
      overflow: "hidden",
      background: dark ? "rgba(255,255,255,0.025)" : "#FFFFFF",
      border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.10)"}`,
      transition: "border-color .15s, box-shadow .15s",
      "&:hover": {
        borderColor: dark ? "rgba(91,124,255,0.30)" : "rgba(61,92,253,0.28)",
      },
      "&:focus-within": {
        borderColor: dark ? "#5B7CFF" : "#3D5CFD",
        boxShadow: dark
          ? "0 0 0 3px rgba(91,124,255,0.14)"
          : "0 0 0 3px rgba(61,92,253,0.10)",
      },
      "& .ql-toolbar.ql-snow": {
        borderBottom: dark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
        borderTop: "none",
        borderLeft: "none",
        borderRight: "none",
        padding: "8px 10px",
        background: dark ? "rgba(255,255,255,0.015)" : "rgba(15,23,42,0.02)",
      },
      "& .ql-container.ql-snow": {
        border: "none",
        fontSize: 14,
        fontFamily: "inherit",
      },
      "& .ql-editor": {
        minHeight: 120,
        maxHeight: 280,
        color: dark ? "#FAFAFA" : "#0F172A",
        padding: "12px 14px",
        lineHeight: 1.55,
      },
      "& .ql-editor.ql-blank::before": {
        color: dark ? "rgba(255,255,255,0.32)" : "#94A3B8",
        fontStyle: "normal",
        left: 14,
        right: 14,
      },
      "& .ql-snow .ql-stroke": { stroke: dark ? "rgba(255,255,255,0.65)" : "#475569" },
      "& .ql-snow .ql-fill": { fill: dark ? "rgba(255,255,255,0.65)" : "#475569" },
      "& .ql-snow .ql-picker": { color: dark ? "rgba(255,255,255,0.65)" : "#475569" },
      "& .ql-snow button:hover .ql-stroke, & .ql-snow .ql-picker-label:hover .ql-stroke": {
        stroke: dark ? "#A5B4FC" : "#3D5CFD",
      },
      "& .ql-snow button:hover .ql-fill, & .ql-snow .ql-picker-label:hover .ql-fill": {
        fill: dark ? "#A5B4FC" : "#3D5CFD",
      },
      "& .ql-snow button.ql-active .ql-stroke, & .ql-snow .ql-picker-label.ql-active .ql-stroke": {
        stroke: dark ? "#A5B4FC" : "#3D5CFD",
      },
      "& .ql-snow button.ql-active .ql-fill, & .ql-snow .ql-picker-label.ql-active .ql-fill": {
        fill: dark ? "#A5B4FC" : "#3D5CFD",
      },
      "& .ql-snow .ql-picker-options": {
        background: dark ? "#1C1F26" : "#FFFFFF",
        border: dark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(15,23,42,0.08)",
        borderRadius: 8,
      },
      "& .ql-editor pre.ql-syntax, & .ql-editor pre": {
        background: dark ? "rgba(0,0,0,0.35)" : "rgba(15,23,42,0.04)",
        color: dark ? "#FAFAFA" : "#0F172A",
        borderRadius: 8,
        padding: "10px 12px",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        fontSize: 13,
      },
      "& .ql-editor blockquote": {
        borderLeft: `3px solid ${dark ? "#5B7CFF" : "#3D5CFD"}`,
        paddingLeft: 12,
        color: dark ? "rgba(255,255,255,0.75)" : "#475569",
        marginLeft: 0,
        marginRight: 0,
      },
    },

    // ── Custom field row ──
    fieldRow: { display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 8 },
    fieldTypeChip: {
      fontSize: 10,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.04em",
      padding: "3px 7px",
      borderRadius: 6,
      whiteSpace: "nowrap",
      marginTop: 10,
      flexShrink: 0,
    },
    chipText: {
      background: dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.05)",
      color: dark ? "rgba(255,255,255,0.55)" : "#71717A",
    },
    chipWhatsapp: { background: "rgba(37,211,102,0.14)", color: "#25d366" },
    chipCpf: { background: "rgba(91,124,255,0.14)", color: "#5B7CFF" },
    chipEmail: { background: "rgba(245,158,11,0.14)", color: "#f59e0b" },
    chipCurrency: { background: "rgba(34,197,94,0.14)", color: "#22c55e" },
    showOnCardDot: {
      width: 6, height: 6, borderRadius: "50%",
      backgroundColor: "#22c55e",
      flexShrink: 0,
      marginTop: 16,
      boxShadow: "0 0 0 2px rgba(34,197,94,0.25)",
    },
    fieldMenu: {
      padding: 6,
      borderRadius: 7,
      color: dark ? "rgba(255,255,255,0.40)" : "#94A3B8",
      marginTop: 6,
      transition: "background .12s, color .12s",
      "&:hover": {
        color: dark ? "#FAFAFA" : "#0F172A",
        background: dark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.04)",
      },
    },
    addFieldBtn: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      width: "100%",
      padding: "10px 14px",
      borderRadius: 10,
      border: `1.5px dashed ${dark ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.10)"}`,
      background: "transparent",
      color: dark ? "rgba(255,255,255,0.55)" : "#71717A",
      cursor: "pointer",
      transition: "background .12s, border-color .12s, color .12s",
      fontSize: 12.5,
      fontWeight: 500,
      fontFamily: "inherit",
      "&:hover": {
        background: dark ? "rgba(91,124,255,0.06)" : "rgba(61,92,253,0.04)",
        borderColor: dark ? "rgba(91,124,255,0.32)" : "rgba(61,92,253,0.30)",
        color: dark ? "#A5B4FC" : "#3D5CFD",
      },
    },
    labelEditor: {
      "& .MuiInputBase-input": { fontSize: 13, fontWeight: 500, padding: "10px 14px" },
      "& .MuiOutlinedInput-root": { borderRadius: 10 },
      width: "100%",
    },

    // ── Add Field Popover (premium) ──
    addFieldPopover: {
      "& .MuiPopover-paper": {
        borderRadius: 14,
        background: dark ? "#1C1F26" : "#FFFFFF",
        border: dark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.08)",
        boxShadow: dark
          ? "0 20px 60px -12px rgba(0,0,0,0.6), 0 8px 24px -6px rgba(0,0,0,0.4)"
          : "0 20px 60px -12px rgba(15,23,42,0.18), 0 8px 24px -6px rgba(15,23,42,0.10)",
        width: 320,
        overflow: "hidden",
      },
    },
    addFieldHeader: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "12px 14px 10px",
      borderBottom: dark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
    },
    addFieldTitle: {
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      color: dark ? "rgba(255,255,255,0.65)" : "#475569",
    },
    addFieldSection: { padding: "8px 8px" },
    addFieldSectionLabel: {
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: dark ? "rgba(255,255,255,0.42)" : "#94A3B8",
      padding: "4px 10px 6px",
    },
    addFieldDivider: {
      height: 1,
      background: dark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.05)",
      margin: "0 14px",
    },
    addFieldPresetRow: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "10px 12px",
      borderRadius: 9,
      border: "none",
      background: "transparent",
      width: "100%",
      cursor: "pointer",
      transition: "background .12s",
      fontFamily: "inherit",
      textAlign: "left",
      "&:hover": { background: dark ? "rgba(91,124,255,0.10)" : "rgba(61,92,253,0.06)" },
    },
    addFieldPresetIcon: {
      width: 32, height: 32, borderRadius: 9,
      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    },
    addFieldRowText: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 },
    addFieldRowTitle: { fontSize: 13, fontWeight: 600, color: dark ? "#FAFAFA" : "#0F172A" },
    addFieldRowDesc: {
      fontSize: 11,
      color: dark ? "rgba(255,255,255,0.45)" : "#94A3B8",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    addFieldTypeGrid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 4,
      padding: "0 4px",
    },
    addFieldTypeRow: {
      display: "flex",
      alignItems: "center",
      gap: 9,
      padding: "8px 10px",
      borderRadius: 8,
      border: "none",
      background: "transparent",
      width: "100%",
      cursor: "pointer",
      transition: "background .12s, color .12s",
      fontFamily: "inherit",
      textAlign: "left",
      color: dark ? "rgba(255,255,255,0.78)" : "#334155",
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.04)",
        color: dark ? "#FAFAFA" : "#0F172A",
      },
    },
    addFieldTypeIcon: { flexShrink: 0, display: "flex" },
    addFieldTypeLabel: { fontSize: 12.5, fontWeight: 500 },

    // ── Checklist (subtarefas) ──
    checklistProgress: { display: "flex", alignItems: "center", gap: 10, marginBottom: 8 },
    checklistBar: {
      flex: 1, height: 5, borderRadius: 3,
      backgroundColor: dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
      overflow: "hidden",
    },
    checklistBarFill: { height: "100%", borderRadius: 3, transition: "width .3s, background-color .3s" },
    checklistPercent: {
      fontSize: 11, fontWeight: 600,
      color: dark ? "rgba(255,255,255,0.55)" : "#71717A",
      minWidth: 36, textAlign: "right", fontVariantNumeric: "tabular-nums",
    },
    checklistItem: {
      display: "flex", alignItems: "center", gap: 4,
      marginBottom: 2, borderRadius: 7, padding: "2px 4px 2px 0",
      transition: "background .12s",
      "&:hover": { background: dark ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.02)" },
      "&:hover $checklistDeleteBtn": { opacity: 1 },
    },
    checklistCheckbox: {
      padding: 6,
      color: dark ? "rgba(255,255,255,0.32)" : "#94A3B8",
      "&.Mui-checked": { color: "#22c55e" },
    },
    checklistText: { flex: 1, fontSize: 13, color: dark ? "rgba(255,255,255,0.85)" : "#334155" },
    checklistTextDone: {
      flex: 1, fontSize: 13,
      color: dark ? "rgba(255,255,255,0.32)" : "#94A3B8",
      textDecoration: "line-through",
    },
    checklistDeleteBtn: {
      padding: 4, opacity: 0, transition: "opacity .12s, color .12s",
      color: dark ? "rgba(255,255,255,0.32)" : "#94A3B8",
      "&:hover": { color: "#ef4444" },
    },
    checklistAddRow: { display: "flex", alignItems: "center", gap: 6, marginTop: 6 },
    checklistInput: {
      "& .MuiInputBase-input": { fontSize: 13, padding: "8px 12px" },
      "& .MuiOutlinedInput-root": { borderRadius: 8 },
    },
    inlineBtn: {
      padding: "6px 12px", borderRadius: 8, border: "none",
      fontSize: 12, fontWeight: 600, cursor: "pointer",
      transition: "background .12s", fontFamily: "inherit",
    },
    inlineBtnPrimary: {
      background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
      color: "#FFFFFF",
      "&:hover:not(:disabled)": {
        background: "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)",
      },
      "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
    },
    inlineBtnGhost: {
      background: "transparent",
      color: dark ? "rgba(255,255,255,0.55)" : "#71717A",
      "&:hover": { background: dark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.04)" },
    },
    checklistAddBtn: {
      display: "flex", alignItems: "center", gap: 5,
      padding: "6px 10px", borderRadius: 7, border: "none", background: "transparent",
      color: dark ? "rgba(255,255,255,0.45)" : "#71717A",
      cursor: "pointer", fontSize: 12, fontWeight: 500,
      transition: "background .12s, color .12s", fontFamily: "inherit",
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.03)",
        color: dark ? "#A5B4FC" : "#3D5CFD",
      },
    },

    // ── Field menu (3-dot) options popover ──
    fieldMenuPopover: {
      "& .MuiPopover-paper": {
        borderRadius: 12,
        background: dark ? "#1C1F26" : "#FFFFFF",
        border: dark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.08)",
        boxShadow: dark
          ? "0 12px 32px -8px rgba(0,0,0,0.6)"
          : "0 12px 32px -8px rgba(15,23,42,0.18)",
        minWidth: 200,
        padding: 6,
      },
    },
    fieldMenuItem: {
      display: "flex",
      alignItems: "center",
      gap: 9,
      padding: "8px 10px",
      borderRadius: 7,
      border: "none",
      background: "transparent",
      width: "100%",
      cursor: "pointer",
      transition: "background .12s",
      fontFamily: "inherit",
      textAlign: "left",
      color: dark ? "rgba(255,255,255,0.78)" : "#334155",
      fontSize: 12.5,
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.04)",
        color: dark ? "#FAFAFA" : "#0F172A",
      },
    },
    fieldMenuItemDanger: {
      color: "#ef4444",
      "&:hover": {
        background: "rgba(239,68,68,0.08)",
        color: "#ef4444",
      },
    },

    // ── Shell do modal (header micro + container do título HERO) ──
    modalHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      padding: "12px 20px 8px",
      flexShrink: 0,
    },
    modalHeaderCrumb: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "3px 9px",
      borderRadius: 999,
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: dark ? "rgba(255,255,255,0.55)" : "#71717A",
      background: dark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)",
      border: dark ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(15,23,42,0.05)",
    },
    modalHeaderCrumbDot: {
      width: 6, height: 6, borderRadius: "50%",
      background: dark ? "#7C9BFF" : "#3D5CFD",
      flexShrink: 0,
    },
    modalHeaderCloseBtn: {
      width: 32, height: 32,
      border: "none",
      background: "transparent",
      borderRadius: 8,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      color: dark ? "rgba(255,255,255,0.55)" : "#71717A",
      transition: "background .15s, color .15s",
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.04)",
        color: dark ? "#FAFAFA" : "#0F172A",
      },
    },
    modalTitleHero: {
      padding: "0 24px 8px",
    },

    // ── Footer sticky (Excluir | Resumo SLA | Fechar/Salvar) ──
    modalFooter: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      padding: "14px 20px",
      borderTop: dark ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(15,23,42,0.06)",
      background: dark ? "rgba(0,0,0,0.22)" : "rgba(15,23,42,0.02)",
      flexShrink: 0,
    },
    modalFooterLeft: { display: "flex", alignItems: "center", gap: 8, flexShrink: 0 },
    modalFooterCenter: {
      flex: 1, minWidth: 0,
      display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
      fontSize: 12,
      color: dark ? "rgba(255,255,255,0.45)" : "#94A3B8",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    modalFooterActions: { display: "flex", alignItems: "center", gap: 8, flexShrink: 0 },
    modalDeleteBtn: {
      width: 36, height: 36, padding: 0,
      border: dark ? "1px solid rgba(239,68,68,0.32)" : "1px solid rgba(239,68,68,0.28)",
      background: "transparent",
      borderRadius: 10,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#ef4444",
      transition: "background .15s, border-color .15s",
      "&:hover": {
        background: "rgba(239,68,68,0.10)",
        borderColor: "rgba(239,68,68,0.48)",
      },
    },
    modalCancelBtn: {
      height: 38,
      padding: "0 18px",
      borderRadius: 10,
      border: dark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(15,23,42,0.10)",
      background: "transparent",
      color: dark ? "rgba(255,255,255,0.75)" : "#475569",
      fontSize: 13,
      fontWeight: 500,
      cursor: "pointer",
      transition: "background .12s, color .12s",
      fontFamily: "inherit",
      "&:hover:not(:disabled)": {
        background: dark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)",
        color: dark ? "#FAFAFA" : "#0F172A",
      },
      "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
    },
    modalSubmitBtn: {
      height: 38,
      padding: "0 20px",
      borderRadius: 10,
      border: "none",
      background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
      color: "#FFFFFF",
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: 7,
      transition: "transform .12s, opacity .15s, box-shadow .15s",
      fontFamily: "inherit",
      position: "relative",
      boxShadow: "0 4px 14px -2px rgba(61,92,253,0.36)",
      "&:hover:not(:disabled)": {
        transform: "translateY(-1px)",
        boxShadow: "0 8px 22px -4px rgba(61,92,253,0.48)",
      },
      "&:disabled": { opacity: 0.45, cursor: "not-allowed", boxShadow: "none" },
    },

    // ── Status pill (segmented control no body) ──
    statusPills: {
      display: "flex",
      flexWrap: "wrap",
      gap: 6,
    },
    statusPill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "6px 11px",
      borderRadius: 999,
      border: dark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(15,23,42,0.08)",
      background: "transparent",
      color: dark ? "rgba(255,255,255,0.65)" : "#475569",
      fontSize: 12,
      fontWeight: 600,
      cursor: "pointer",
      transition: "background .12s, border-color .12s, color .12s",
      fontFamily: "inherit",
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)",
        color: dark ? "#FAFAFA" : "#0F172A",
      },
    },
    statusPillActive: {
      color: "#FFFFFF",
      borderColor: "transparent",
    },
    statusPillDot: { width: 6, height: 6, borderRadius: "50%", flexShrink: 0 },

    // ── Section title (igual ao CreateLeadModal) ──
    sectionTitleSlim: {
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: dark ? "rgba(255,255,255,0.42)" : "#71717A",
      marginBottom: 10,
      display: "block",
    },
  };
});

// ─────────────────────────────────────────────────────────────────────────────
// Constantes de campos personalizados
// ─────────────────────────────────────────────────────────────────────────────
export const FIELD_TYPES = {
  text: { label: "Texto", icon: TypeIcon, iconColor: "#94A3B8", chipClass: "chipText" },
  whatsapp: { label: "WhatsApp", icon: WhatsappIconLc, iconColor: "#25d366", chipClass: "chipWhatsapp" },
  cpf: { label: "CPF", icon: CpfIconLc, iconColor: "#5B7CFF", chipClass: "chipCpf" },
  email: { label: "Email", icon: EmailIconLc, iconColor: "#f59e0b", chipClass: "chipEmail" },
  currency: { label: "Moeda", icon: CurrencyIconLc, iconColor: "#22c55e", chipClass: "chipCurrency" },
  checklist: { label: "Subtarefas", icon: ChecklistIconLc, iconColor: "#A5B4FC", chipClass: "chipText" },
};

export const PRESETS = {
  leads: {
    label: "Leads",
    description: "Nome · CPF · WhatsApp · Email · Endereço · Valor",
    icon: UsersIconLc,
    accent: "#5B7CFF",
    fields: [
      { type: "text", label: "Nome" },
      { type: "cpf", label: "CPF" },
      { type: "whatsapp", label: "WhatsApp" },
      { type: "email", label: "Email" },
      { type: "text", label: "Endereço" },
      { type: "currency", label: "Valor" },
    ],
  },
};

// Quill — toolbar enxuta, focada nas formatações úteis pra um card.
export const QUILL_MODULES = {
  toolbar: [
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["blockquote", "code-block"],
    ["link", "clean"],
  ],
  clipboard: { matchVisual: false },
};
export const QUILL_FORMATS = [
  "bold", "italic", "underline",
  "list", "bullet",
  "blockquote", "code-block",
  "link",
];

// Quill considera "<p><br></p>" como vazio — checamos via textContent.
export const quillIsEmpty = (html) => {
  if (!html) return true;
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  const text = (tmp.textContent || "").trim();
  return text.length === 0;
};

let fieldIdCounter = 0;
export const generateFieldId = () => {
  fieldIdCounter += 1;
  return `f${Date.now()}_${fieldIdCounter}`;
};

// ─────────────────────────────────────────────────────────────────────────────
// UserAvatar — foto real ou fallback iniciais coloridas
// ─────────────────────────────────────────────────────────────────────────────
export const UserAvatar = ({ user, size = 26 }) => {
  const classes = useLeadSharedStyles();
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => { setImgFailed(false); }, [user?.id, user?.profileImage]);

  const color = stringToColor(user?.name);
  const fontSize = Math.max(9, Math.round(size * 0.42));
  const baseStyle = { width: size, height: size, fontSize };

  const hasPhoto = !imgFailed && !!user?.profileImage;

  if (hasPhoto) {
    return (
      <div className={classes.avatar} style={{ ...baseStyle, border: `1px solid ${color}40` }}>
        <img
          src={user.profileImage}
          alt={user.name || ""}
          className={classes.avatarImg}
          onError={() => setImgFailed(true)}
          loading="lazy"
        />
      </div>
    );
  }

  return (
    <div
      className={classes.avatar}
      style={{
        ...baseStyle,
        background: `${color}26`,
        color,
        border: `1px solid ${color}40`,
      }}
    >
      {initials(user?.name)}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// AssigneePicker — popover com busca + avatares
// ─────────────────────────────────────────────────────────────────────────────
export const AssigneePicker = ({ users, value, onChange, loading, placeholder = "Ninguém" }) => {
  const classes = useLeadSharedStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [search, setSearch] = useState("");
  const inputRef = useRef(null);

  const open = Boolean(anchorEl);
  const selected = useMemo(
    () => users.find((u) => u.id === value) || null,
    [users, value]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => (u.name || "").toLowerCase().includes(q)
      || (u.email || "").toLowerCase().includes(q));
  }, [users, search]);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current && inputRef.current.focus(), 60);
    }
    if (!open) setSearch("");
  }, [open]);

  const openPopover = (e) => setAnchorEl(e.currentTarget);
  const closePopover = () => setAnchorEl(null);

  const pick = (userId) => {
    onChange(userId);
    closePopover();
  };

  return (
    <>
      <button type="button" className={classes.assigneeTrigger} onClick={openPopover}
        aria-haspopup="listbox" aria-expanded={open}>
        {selected ? (
          <>
            <UserAvatar user={selected} size={26} />
            <span className={classes.assigneeName}>{selected.name}</span>
          </>
        ) : (
          <>
            <span className={classes.avatarPlaceholder}
              style={{ width: 26, height: 26 }}>
              <UserRound size={14} strokeWidth={1.75} />
            </span>
            <span className={`${classes.assigneeName} ${classes.assigneeNamePlaceholder}`}>
              {placeholder}
            </span>
          </>
        )}
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={classes.assigneeChevron}>
          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5"
            strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={closePopover}
        className={classes.pickerPopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <div className={classes.pickerSearchWrap}>
          <Search size={14} className={classes.pickerSearchIcon} />
          <input
            ref={inputRef}
            className={classes.pickerSearchInput}
            placeholder="Buscar por nome ou email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Escape") closePopover(); }}
          />
        </div>
        <div className={classes.pickerList}>
          <button
            type="button"
            className={`${classes.pickerOption} ${!value ? classes.pickerOptionActive : ""}`}
            onClick={() => pick("")}
          >
            <span className={classes.avatarPlaceholder} style={{ width: 26, height: 26 }}>
              <UserRound size={14} strokeWidth={1.75} />
            </span>
            <div className={classes.pickerOptionText}>
              <span className={classes.pickerOptionName}>Ninguém</span>
              <span className={classes.pickerOptionMeta}>Sem responsável definido</span>
            </div>
            {!value && <Check size={15} className={classes.pickerCheck} />}
          </button>

          {loading && (
            <div className={classes.pickerEmpty}>
              <CircularProgress size={16} style={{ marginRight: 8 }} />
              Carregando usuários...
            </div>
          )}

          {!loading && filtered.length === 0 && search && (
            <div className={classes.pickerEmpty}>Nenhum usuário encontrado.</div>
          )}

          {!loading && filtered.map((u) => {
            const isActive = value === u.id;
            return (
              <button
                key={u.id}
                type="button"
                className={`${classes.pickerOption} ${isActive ? classes.pickerOptionActive : ""}`}
                onClick={() => pick(u.id)}
              >
                <UserAvatar user={u} size={28} />
                <div className={classes.pickerOptionText}>
                  <span className={classes.pickerOptionName}>{u.name}</span>
                  {(u.email || u.profile) && (
                    <span className={classes.pickerOptionMeta}>
                      {u.profile && (u.profile === "admin" ? "Admin" : u.profile === "superadmin" ? "Super-admin" : "Usuário")}
                      {u.profile && u.email && " · "}
                      {u.email || ""}
                    </span>
                  )}
                </div>
                {isActive && <Check size={15} className={classes.pickerCheck} />}
              </button>
            );
          })}
        </div>
      </Popover>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PrioritySegmented
// ─────────────────────────────────────────────────────────────────────────────
export const PrioritySegmented = ({ value, onChange }) => {
  const classes = useLeadSharedStyles();
  return (
    <div className={classes.prioritySegment}>
      {PRIORITIES.map((p) => {
        const active = value === p.value;
        return (
          <Tooltip key={p.value} title={p.hint} arrow placement="top">
            <button
              type="button"
              className={`${classes.priorityPill} ${active ? classes.priorityPillActive : ""}`}
              onClick={() => onChange(p.value)}
              aria-pressed={active}
            >
              <span className={classes.priorityDot} style={{ backgroundColor: p.color }} />
              {p.label}
            </button>
          </Tooltip>
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SlaPopoverButton — trigger compacto + popover (presets + datetime)
// ─────────────────────────────────────────────────────────────────────────────
export const SlaPopoverButton = ({ value, onChange }) => {
  const classes = useLeadSharedStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const status = useMemo(() => computeSlaStatus(value), [value]);
  const StatusIcon = status?.icon;

  const open = Boolean(anchorEl);
  const openPopover = (e) => setAnchorEl(e.currentTarget);
  const closePopover = () => setAnchorEl(null);

  const matchPreset = (preset) => {
    if (!value) return false;
    return slaPreset(preset.offsetDays, preset.hour) === value;
  };

  const applyPreset = (p) => {
    onChange(slaPreset(p.offsetDays, p.hour));
    closePopover();
  };

  return (
    <>
      <button
        type="button"
        className={classes.slaTrigger}
        onClick={openPopover}
        aria-haspopup="dialog"
        aria-expanded={open}
        style={value && status ? {
          borderColor: `${status.color}55`,
          background: status.bg,
        } : undefined}
      >
        <Calendar size={14} strokeWidth={2}
          style={{ flexShrink: 0, color: status?.color || (anchorEl ? "#5B7CFF" : undefined) }}
        />
        {value ? (
          <>
            <span className={classes.slaTriggerText} style={{ color: status?.color }}>
              SLA: {formatSlaShort(value)}
            </span>
            <Tooltip title="Limpar prazo" arrow>
              <button
                type="button"
                className={classes.slaClearInlineBtn}
                onClick={(e) => { e.stopPropagation(); onChange(""); }}
                aria-label="Remover prazo"
              >
                <X size={12} strokeWidth={2.25} />
              </button>
            </Tooltip>
          </>
        ) : (
          <span className={`${classes.slaTriggerText} ${classes.slaTriggerPlaceholder}`}>
            Definir prazo
          </span>
        )}
      </button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={closePopover}
        className={classes.pickerPopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <div className={classes.slaPopoverHeader}>
          <Calendar size={14} strokeWidth={2} style={{ color: "#5B7CFF" }} />
          <span className={classes.slaPopoverTitle}>Definir prazo</span>
        </div>

        <div className={classes.slaPopoverBody}>
          <div className={classes.slaPresetsRow}>
            {SLA_PRESETS.map((p) => {
              const active = matchPreset(p);
              return (
                <button
                  key={p.key}
                  type="button"
                  className={`${classes.slaPresetCard} ${active ? classes.slaPresetCardActive : ""}`}
                  onClick={() => applyPreset(p)}
                >
                  <Clock size={13} strokeWidth={2} />
                  {p.label}
                </button>
              );
            })}
          </div>

          <div>
            <div className={classes.slaCustomLabel}>Data e hora personalizada</div>
            <TextField
              type="datetime-local"
              value={value || ""}
              onChange={(e) => onChange(e.target.value)}
              variant="outlined" size="small"
              className={classes.slaCustomInput}
              InputLabelProps={{ shrink: true }}
            />
          </div>
        </div>

        {(value || status) && (
          <div className={classes.slaPopoverFooter}>
            {status && StatusIcon && (
              <span
                className={classes.slaPopoverStatus}
                style={{
                  background: status.bg,
                  color: status.color,
                  borderColor: status.border,
                }}
              >
                <StatusIcon size={12} strokeWidth={2.5} />
                <span>{status.label}</span>
              </span>
            )}
            {value && (
              <button
                type="button"
                className={classes.slaPopoverClearBtn}
                onClick={() => onChange("")}
              >
                Limpar
              </button>
            )}
          </div>
        )}
      </Popover>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CardTitleHero — input grande limpo, validação tardia (só após submit)
// ─────────────────────────────────────────────────────────────────────────────
export const CardTitleHero = ({
  value,
  onChange,
  onCommit,
  placeholder = "Dê um título para este card...",
  error = false,
  errorText = "Adicione um título para criar o card.",
  autoFocus = false,
}) => {
  const classes = useLeadSharedStyles();
  return (
    <div>
      <TextField
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={(e) => onCommit && onCommit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            if (onCommit) onCommit(e.target.value);
            e.target.blur();
          }
        }}
        variant="outlined" fullWidth multiline maxRows={3}
        autoFocus={autoFocus}
        className={`${classes.titleHero} ${error ? classes.titleHeroError : ""}`}
      />
      {error && (
        <span className={classes.fieldError}>
          <AlertCircle size={12} strokeWidth={2} />
          {errorText}
        </span>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CardDescriptionEditor — Quill com toolbar enxuta + estilo dark-first
// ─────────────────────────────────────────────────────────────────────────────
export const CardDescriptionEditor = ({
  value,
  onChange,
  onBlur,
  placeholder = "Adicione contexto, próximos passos, decisões importantes...",
}) => {
  const classes = useLeadSharedStyles();
  return (
    <div className={classes.quillWrap}>
      <ReactQuill
        theme="snow"
        value={value || ""}
        onChange={onChange}
        onBlur={onBlur}
        modules={QUILL_MODULES}
        formats={QUILL_FORMATS}
        placeholder={placeholder}
      />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// renderField interno — branch por tipo (mask, currency, email, checklist)
// ─────────────────────────────────────────────────────────────────────────────
const renderFieldInput = ({ field, classes, onChange, checklistState, checklistActions }) => {
  const commonProps = {
    variant: "outlined", size: "small", fullWidth: true,
    className: classes.fieldInput, label: field.label, value: field.value,
  };
  switch (field.type) {
    case "whatsapp":
      return (
        <InputMask mask="(99) 99999-9999" value={field.value}
          onChange={(e) => onChange(field.id, "value", e.target.value)}>
          {(inputProps) => <TextField {...inputProps} {...commonProps} onChange={undefined} />}
        </InputMask>
      );
    case "cpf":
      return (
        <InputMask mask="999.999.999-99" value={field.value}
          onChange={(e) => onChange(field.id, "value", e.target.value)}>
          {(inputProps) => <TextField {...inputProps} {...commonProps} onChange={undefined} />}
        </InputMask>
      );
    case "currency":
      return (
        <NumberFormat customInput={TextField} variant="outlined" size="small" fullWidth
          className={classes.fieldInput} label={field.label}
          value={field.value === "" ? "" : Number(field.value)}
          thousandSeparator="." decimalSeparator="," prefix="R$ " decimalScale={2}
          allowNegative={false} isNumericString={false}
          onValueChange={(values) =>
            onChange(field.id, "value", values.floatValue !== undefined ? String(values.floatValue) : "")
          } />
      );
    case "email":
      return <TextField {...commonProps} type="email"
        onChange={(e) => onChange(field.id, "value", e.target.value)} />;
    case "checklist":
      return renderChecklistField({ field, classes, checklistState, checklistActions });
    default:
      return <TextField {...commonProps}
        onChange={(e) => onChange(field.id, "value", e.target.value)} />;
  }
};

const renderChecklistField = ({ field, classes, checklistState, checklistActions }) => {
  const items = field.items || [];
  const total = items.length;
  const done = items.filter((c) => c.done).length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const { addingFor, newText } = checklistState;
  const { setAddingFor, setNewText, addItem, toggleItem, deleteItem } = checklistActions;
  return (
    <div style={{ width: "100%" }}>
      {total > 0 && (
        <div className={classes.checklistProgress}>
          <div className={classes.checklistBar}>
            <div className={classes.checklistBarFill}
              style={{ width: `${pct}%`, backgroundColor: pct === 100 ? "#22c55e" : "#5B7CFF" }} />
          </div>
          <Typography className={classes.checklistPercent}>{done}/{total}</Typography>
        </div>
      )}
      {items.map((item) => (
        <div key={item.id} className={classes.checklistItem}>
          <Checkbox checked={item.done}
            onChange={() => toggleItem(field.id, item.id)}
            className={classes.checklistCheckbox} size="small" />
          <Typography className={item.done ? classes.checklistTextDone : classes.checklistText}>
            {item.text}
          </Typography>
          <IconButton className={classes.checklistDeleteBtn} size="small"
            onClick={() => deleteItem(field.id, item.id)}>
            <X size={14} />
          </IconButton>
        </div>
      ))}
      {addingFor === field.id ? (
        <div className={classes.checklistAddRow}>
          <TextField className={classes.checklistInput} variant="outlined" size="small" fullWidth
            placeholder="Nome da subtarefa..." value={newText}
            onChange={(e) => setNewText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (newText.trim()) {
                  addItem(field.id, newText.trim());
                  setNewText("");
                }
              }
              if (e.key === "Escape") { setAddingFor(null); setNewText(""); }
            }} autoFocus />
          <button type="button" className={`${classes.inlineBtn} ${classes.inlineBtnPrimary}`}
            onClick={() => {
              if (newText.trim()) {
                addItem(field.id, newText.trim());
                setNewText("");
              }
            }} disabled={!newText.trim()}>
            Criar
          </button>
          <button type="button" className={`${classes.inlineBtn} ${classes.inlineBtnGhost}`}
            onClick={() => { setAddingFor(null); setNewText(""); }}>
            Cancelar
          </button>
        </div>
      ) : (
        <button className={classes.checklistAddBtn} type="button"
          onClick={() => { setAddingFor(field.id); setNewText(""); }}>
          <Plus size={14} /> Adicionar item
        </button>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CardCustomFieldsSection — lista de fields + popover de "add" + menu opções
// State (fields) é controlado pelo pai. Pai injeta handlers.
// ─────────────────────────────────────────────────────────────────────────────
export const CardCustomFieldsSection = ({
  fields,
  onAddField,
  onAddPreset,
  onUpdateField,
  onRemoveField,
  onToggleShowOnCard,
  checklistState,
  checklistActions,
}) => {
  const classes = useLeadSharedStyles();
  const [addAnchor, setAddAnchor] = useState(null);
  const [fieldMenuAnchor, setFieldMenuAnchor] = useState(null);
  const [activeFieldId, setActiveFieldId] = useState(null);
  const [editingLabelFieldId, setEditingLabelFieldId] = useState(null);

  const activeField = useMemo(
    () => fields.find((f) => f.id === activeFieldId) || null,
    [fields, activeFieldId]
  );

  const closeAdd = () => setAddAnchor(null);
  const closeFieldMenu = () => { setFieldMenuAnchor(null); setActiveFieldId(null); };

  const handleAddPreset = (key) => { onAddPreset(key); closeAdd(); };
  const handleAddType = (type) => { onAddField(type, FIELD_TYPES[type].label); closeAdd(); };

  const getChipClass = (type) => {
    const info = FIELD_TYPES[type];
    return info ? classes[info.chipClass] : classes.chipText;
  };

  return (
    <div>
      {fields.length > 0 && fields.map((field) => (
        <div key={field.id} className={classes.fieldRow}>
          {field.showOnCard && (
            <div className={classes.showOnCardDot} title="Aparece no card" />
          )}
          <span className={`${classes.fieldTypeChip} ${getChipClass(field.type)}`}>
            {FIELD_TYPES[field.type]?.label || "Texto"}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            {editingLabelFieldId === field.id ? (
              <TextField
                className={classes.labelEditor}
                variant="outlined" size="small"
                value={field.label}
                onChange={(e) => onUpdateField(field.id, "label", e.target.value)}
                onBlur={() => setEditingLabelFieldId(null)}
                onKeyDown={(e) => { if (e.key === "Enter") setEditingLabelFieldId(null); }}
                autoFocus placeholder="Nome do campo"
              />
            ) : (
              renderFieldInput({
                field, classes, onChange: onUpdateField,
                checklistState, checklistActions,
              })
            )}
          </div>
          <IconButton className={classes.fieldMenu} size="small"
            onClick={(e) => { setActiveFieldId(field.id); setFieldMenuAnchor(e.currentTarget); }}>
            <MoreVertical size={16} />
          </IconButton>
        </div>
      ))}

      <button className={classes.addFieldBtn} onClick={(e) => setAddAnchor(e.currentTarget)} type="button">
        <Plus size={14} strokeWidth={2} /> Adicionar campo
      </button>

      <Popover
        anchorEl={addAnchor} open={Boolean(addAnchor)} onClose={closeAdd}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        className={classes.addFieldPopover}
      >
        <div className={classes.addFieldHeader}>
          <Plus size={14} strokeWidth={2.25} style={{ color: "#5B7CFF" }} />
          <span className={classes.addFieldTitle}>Adicionar campo</span>
        </div>

        <div className={classes.addFieldSection}>
          <div className={classes.addFieldSectionLabel}>Modelo pronto</div>
          {Object.entries(PRESETS).map(([key, preset]) => {
            const PresetIcon = preset.icon;
            return (
              <button
                key={key}
                type="button"
                className={classes.addFieldPresetRow}
                onClick={() => handleAddPreset(key)}
              >
                <span className={classes.addFieldPresetIcon}
                  style={{
                    background: `${preset.accent}1F`,
                    color: preset.accent,
                    border: `1px solid ${preset.accent}33`,
                  }}>
                  <PresetIcon size={15} strokeWidth={1.75} />
                </span>
                <div className={classes.addFieldRowText}>
                  <span className={classes.addFieldRowTitle}>{preset.label}</span>
                  <span className={classes.addFieldRowDesc}>{preset.description}</span>
                </div>
              </button>
            );
          })}
        </div>

        <div className={classes.addFieldDivider} />

        <div className={classes.addFieldSection}>
          <div className={classes.addFieldSectionLabel}>Tipos de campo</div>
          <div className={classes.addFieldTypeGrid}>
            {Object.entries(FIELD_TYPES).map(([type, info]) => {
              const TypeIc = info.icon;
              return (
                <button
                  key={type}
                  type="button"
                  className={classes.addFieldTypeRow}
                  onClick={() => handleAddType(type)}
                >
                  <span className={classes.addFieldTypeIcon} style={{ color: info.iconColor }}>
                    <TypeIc size={14} strokeWidth={1.75} />
                  </span>
                  <span className={classes.addFieldTypeLabel}>{info.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </Popover>

      <Popover
        anchorEl={fieldMenuAnchor} open={Boolean(fieldMenuAnchor)} onClose={closeFieldMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        className={classes.fieldMenuPopover}
      >
        <button
          type="button"
          className={classes.fieldMenuItem}
          onClick={() => {
            if (activeFieldId) onToggleShowOnCard(activeFieldId);
            closeFieldMenu();
          }}
        >
          <Eye size={14} strokeWidth={1.75} />
          <span style={{ flex: 1 }}>Exibir no card</span>
          <Checkbox
            checked={activeField?.showOnCard || false}
            size="small" color="primary"
            style={{ padding: 0, marginLeft: 8 }}
          />
        </button>
        {activeField?.type === "text" && (
          <button
            type="button"
            className={classes.fieldMenuItem}
            onClick={() => {
              if (activeFieldId) setEditingLabelFieldId(activeFieldId);
              closeFieldMenu();
            }}
          >
            <Pencil size={14} strokeWidth={1.75} />
            <span>Renomear campo</span>
          </button>
        )}
        <button
          type="button"
          className={`${classes.fieldMenuItem} ${classes.fieldMenuItemDanger}`}
          onClick={() => {
            if (activeFieldId) onRemoveField(activeFieldId);
            closeFieldMenu();
          }}
        >
          <Trash2 size={14} strokeWidth={1.75} />
          <span>Remover campo</span>
        </button>
      </Popover>
    </div>
  );
};

